

import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { usePostsQuery } from '../../hooks/usePosts';
import type { Post } from '../../types';
import { POST_STATUS_COLORS, POST_STATUS_LABELS } from '../../constants';
import { PostListFilters } from './PostListFilters';

export const PostList = () => {
  const navigate = useNavigate();
  const { data, isLoading } = usePostsQuery();

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Blog Posts</h1>
        <button
          onClick={() => navigate('/blog/new')}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-md flex items-center space-x-2 text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          <span>New Post</span>
        </button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="p-4 rounded-xl border bg-card text-card-foreground shadow-sm">
          <p className="text-sm text-muted-foreground font-medium">Total Posts</p>
          <p className="text-2xl font-bold mt-2">{data?.total || 0}</p>
        </div>
        <div className="p-4 rounded-xl border bg-card text-card-foreground shadow-sm">
          <p className="text-sm text-muted-foreground font-medium">Published</p>
          <p className="text-2xl font-bold mt-2">{data?.data.filter((p: Post) => p.status === 'PUBLISHED').length || 0}</p>
        </div>
        <div className="p-4 rounded-xl border bg-card text-card-foreground shadow-sm">
          <p className="text-sm text-muted-foreground font-medium">Drafts</p>
          <p className="text-2xl font-bold mt-2">{data?.data.filter((p: Post) => p.status === 'DRAFT').length || 0}</p>
        </div>
        <div className="p-4 rounded-xl border bg-card text-card-foreground shadow-sm">
          <p className="text-sm text-muted-foreground font-medium">Scheduled</p>
          <p className="text-2xl font-bold mt-2">{data?.data.filter((p: Post) => p.status === 'SCHEDULED').length || 0}</p>
        </div>
      </div>

      <PostListFilters />

      <div className="border rounded-md overflow-hidden bg-card text-card-foreground">
        <table className="w-full text-sm text-left">
          <thead className="bg-muted text-muted-foreground text-xs uppercase">
            <tr>
              <th className="px-6 py-3 font-medium">Title</th>
              <th className="px-6 py-3 font-medium">Status</th>
              <th className="px-6 py-3 font-medium">Author</th>
              <th className="px-6 py-3 font-medium">Date</th>
              <th className="px-6 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Loading posts...</td></tr>
            ) : !data?.data || data?.data.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No posts found.</td></tr>
            ) : (
              data?.data.map((post: Post) => (
                <tr key={post.id} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-6 py-4 font-medium">{post.title}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${POST_STATUS_COLORS[post.status as keyof typeof POST_STATUS_COLORS]}`}>
                      {POST_STATUS_LABELS[post.status as keyof typeof POST_STATUS_LABELS]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{post.authorName}</td>
                  <td className="px-6 py-4 text-muted-foreground">{new Date(post.createdAt).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => navigate(`/blog/edit/${post.id}`)} className="text-blue-500 hover:underline mr-3">Edit</button>
                    <button className="text-red-500 hover:underline">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
